import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "@phosphor-icons/react";
import { Reveal } from "./Reveal";

const FAQS = [
  {
    q: "What is Google Business Profile optimization?",
    a: "It's the process of completing, clarifying, and improving everything on your Google Business Profile — your description, services, categories, photos, and posts — so customers who find you on Google Search and Maps get an accurate, compelling reason to choose you.",
  },
  {
    q: "Will this help my business rank higher on Google Maps?",
    a: "A complete, well-written profile is one of the factors Google considers, so it can improve how you show up. But no one can honestly guarantee specific rankings, calls, or sales — be cautious of anyone who does. What we deliver is a profile that's accurate, complete, and built to convert the people who see it.",
  },
  {
    q: "Do I need to give you my Google password?",
    a: "No — never. We work through Google's official manager access. You add us as a manager on your profile, and you can remove that access at any time. Your password stays private.",
  },
  {
    q: "How long does the makeover take?",
    a: "Once we have your intake details and the right profile access, work moves quickly. We'll confirm a clear timeline with you in writing before anything starts, and nothing is published without your approval.",
  },
  {
    q: "Do I need monthly management?",
    a: "No. The Profile Makeover is a one-time project. Monthly support is optional — it's there if you'd like fresh posts, review response drafts, and a simple report without adding to your own to-do list.",
  },
  {
    q: "What if I already have a Google Business Profile?",
    a: "That's exactly who this is for. Most established profiles are incomplete or undersell the business. We build on what you already have, and nothing gets changed or removed without your sign-off.",
  },
];

export const FAQ = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="py-24 sm:py-32">
      <div className="mx-auto max-w-4xl px-6 sm:px-8 lg:px-12">
        <Reveal>
          <p className="eyebrow mb-5">FAQ</p>
          <h2 className="text-4xl font-extrabold tracking-tight text-ink sm:text-5xl">
            Straight Answers, No Fine Print.
          </h2>
        </Reveal>
        <div className="mt-12">
          {FAQS.map((f, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="border-b border-ink/10">
                <button
                  data-testid={`faq-trigger-${i}`}
                  onClick={() => setOpen(open === i ? -1 : i)}
                  aria-expanded={open === i}
                  className="flex w-full items-center justify-between gap-6 py-7 text-left"
                >
                  <span className="font-heading text-xl font-bold tracking-tight text-ink sm:text-2xl">
                    {f.q}
                  </span>
                  <motion.span
                    animate={{ rotate: open === i ? 45 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ink/15 text-pine"
                  >
                    <Plus size={18} weight="bold" />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p
                        data-testid={`faq-answer-${i}`}
                        className="max-w-2xl pb-7 text-base leading-relaxed text-soot"
                      >
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
